import apiRequest from '@/utils/api'

/**
 * 邮件账户管理API服务
 */
class EmailAccountService {
  
  /**
   * 获取邮件账户列表
   * @param {Object} params - 查询参数
   * @param {string} params.search - 搜索关键词
   * @param {string} params.provider - 提供商筛选
   * @param {string} params.group - 分组筛选
   * @param {number} params.page - 页码
   * @param {number} params.page_size - 每页条数
   * @returns {Promise<Object>} 邮件账户列表响应
   */
  async getEmailAccounts(params = {}) {
    try {
      const response = await apiRequest.get('/api/email_accounts/v2/accounts/', {
        params: {
          search: params.search || '',
          provider: params.provider || '',
          group: params.group || '',
          page: params.page || 1,
          page_size: params.page_size || 10,
          ordering: params.ordering || '-created_at'
        }
      })
      return {
        success: true,
        results: response.data.results || [],
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
        page: params.page || 1,
        page_size: params.page_size || 10
      }
    } catch (error) {
      console.error('获取邮件账户列表失败:', error)
      throw error
    }
  }

  /**
   * 获取单个邮件账户详情
   * @param {number} accountId - 账户ID
   * @returns {Promise<Object>} 邮件账户详情
   */
  async getEmailAccount(accountId) {
    try {
      const response = await apiRequest.get(`/api/email_accounts/v2/accounts/${accountId}/`)
      return response.data
    } catch (error) {
      console.error('获取邮件账户详情失败:', error)
      throw error
    }
  }

  /**
   * 创建邮件账户
   * @param {Object} accountData - 账户数据
   * @param {string} accountData.email - 邮箱地址
   * @param {string} accountData.password - 邮箱密码或授权码
   * @param {string} accountData.provider - 服务提供商
   * @param {string} accountData.name - 账户名称
   * @param {Object} accountData.imap_settings - IMAP设置
   * @param {Object} accountData.smtp_settings - SMTP设置
   * @returns {Promise<Object>} 创建的邮件账户
   */
  async createEmailAccount(accountData) {
    try {
      const response = await apiRequest.post('/api/email_accounts/v2/accounts/', accountData)
      return response.data
    } catch (error) {
      console.error('创建邮件账户失败:', error)
      throw error
    }
  }

  /**
   * 更新邮件账户
   * @param {number} accountId - 账户ID
   * @param {Object} accountData - 更新的账户数据
   * @returns {Promise<Object>} 更新结果
   */
  async updateEmailAccount(accountId, accountData) {
    try {
      const response = await apiRequest.put(`/api/email_accounts/v2/accounts/${accountId}/`, accountData)
      return { success: true, data: response.data }
    } catch (error) {
      console.error('更新邮件账户失败:', error)
      return { success: false, error: error.message, response: error.response }
    }
  }

  /**
   * 删除邮件账户
   * @param {number} accountId - 账户ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteEmailAccount(accountId) {
    try {
      const response = await apiRequest.delete(`/api/email_accounts/v2/accounts/${accountId}/`)
      return response.data
    } catch (error) {
      console.error('删除邮件账户失败:', error)
      throw error
    }
  }

  /**
   * 测试邮件账户连接
   * @param {number} accountId - 账户ID
   * @param {string} testType - 测试类型 ('imap', 'smtp', 'both')
   * @returns {Promise<Object>} 测试结果
   */
  async testEmailAccountConnection(accountId, testType = 'both') {
    try {
      const response = await apiRequest.post(`/api/email-accounts/${accountId}/test-connection/`, {
        test_type: testType
      })
      return response.data
    } catch (error) {
      console.error('测试邮件账户连接失败:', error)
      throw error
    }
  }

  /**
   * 同步邮件账户
   * @param {number} accountId - 账户ID
   * @param {Object} syncOptions - 同步选项
   * @param {boolean} syncOptions.force - 是否强制同步
   * @param {number} syncOptions.days - 同步最近几天的邮件
   * @returns {Promise<Object>} 同步结果
   */
  async syncEmailAccount(accountId, syncOptions = {}) {
    try {
      const response = await apiRequest.post(`/api/email_accounts/v2/accounts/${accountId}/sync/`, {
        force: syncOptions.force || false,
        days: syncOptions.days || 7
      })
      return {
        success: true,
        message: response.data.message || '邮件同步成功',
        data: response.data
      }
    } catch (error) {
      console.error('同步邮件账户失败:', error)
      throw error
    }
  }

  /**
   * 发送测试邮件
   * @param {number} accountId - 账户ID
   * @param {Object} testData - 测试邮件数据
   * @param {string} testData.to_email - 收件人邮箱
   * @param {string} testData.subject - 邮件主题
   * @param {string} testData.content - 邮件内容
   * @returns {Promise<Object>} 发送结果
   */
  async sendTestEmail(accountId, testData = {}) {
    try {
      const defaultTestData = {
        recipient: testData.recipient || testData.to_email || 'test@example.com',
        subject: testData.subject || '邮件发送测试',
        content: testData.content || '这是一封来自 BUBU.IM 系统的测试邮件。'
      }
      
      const response = await apiRequest.post(`/api/email_accounts/v2/accounts/${accountId}/send_test/`, defaultTestData)
      return {
        success: true,
        message: response.data.message || '测试邮件发送成功',
        data: response.data
      }
    } catch (error) {
      console.error('发送测试邮件失败:', error)
      throw error
    }
  }

  /**
   * 同步客户信息
   * @param {number} accountId - 账户ID
   * @param {Object} syncOptions - 同步选项
   * @param {boolean} syncOptions.include_sent - 是否包含发件箱邮件
   * @param {boolean} syncOptions.update_existing - 是否更新已存在的客户
   * @param {Array<number>} syncOptions.tag_ids - 标签ID列表
   * @param {Array<number>} syncOptions.group_ids - 分组ID列表
   * @param {Array<string>} syncOptions.name_filters - 姓名排除关键词列表（黑名单）
   * @param {Array<string>} syncOptions.email_filters - 邮箱排除关键词列表（黑名单）
   * @returns {Promise<Object>} 同步结果
   */
  async syncCustomers(accountId, syncOptions = {}) {
    try {
      const response = await apiRequest.post(`/api/email_accounts/v2/accounts/${accountId}/sync_customers/`, {
        include_sent: syncOptions.include_sent !== undefined ? syncOptions.include_sent : true,
        update_existing: syncOptions.update_existing || false,
        tag_ids: syncOptions.tag_ids || null,
        group_ids: syncOptions.group_ids || null,
        name_filters: syncOptions.name_filters || null,
        email_filters: syncOptions.email_filters || null
      })
      return {
        success: true,
        message: response.data.message || '客户同步成功',
        data: response.data.data
      }
    } catch (error) {
      console.error('同步客户失败:', error)
      throw error
    }
  }

  /**
   * 获取邮件账户统计信息
   * @param {number} accountId - 账户ID
   * @param {Object} statsOptions - 统计选项
   * @param {string} statsOptions.period - 统计周期 ('day', 'week', 'month')
   * @returns {Promise<Object>} 统计信息
   */
  async getEmailAccountStats(accountId, statsOptions = {}) {
    try {
      const response = await apiRequest.get(`/api/email-accounts/${accountId}/stats/`, {
        params: {
          period: statsOptions.period || 'month'
        }
      })
      return response.data
    } catch (error) {
      console.error('获取邮件账户统计失败:', error)
      throw error
    }
  }

  /**
   * 获取支持的邮件服务提供商列表
   * @returns {Promise<Array>} 提供商列表
   */
  async getSupportedProviders() {
    try {
      const response = await apiRequest.get('/api/email-accounts/providers/')
      return response.data
    } catch (error) {
      console.error('获取邮件服务提供商列表失败:', error)
      throw error
    }
  }

  /**
   * 获取提供商的预设配置
   * @param {string} provider - 提供商标识
   * @returns {Promise<Object>} 预设配置
   */
  async getProviderPreset(provider) {
    try {
      const response = await apiRequest.get(`/api/email-accounts/providers/${provider}/preset/`)
      return response.data
    } catch (error) {
      console.error('获取提供商预设配置失败:', error)
      throw error
    }
  }

  /**
   * 批量操作邮件账户
   * @param {Object} batchData - 批量操作数据
   * @param {Array<number>} batchData.account_ids - 账户ID列表
   * @param {string} batchData.action - 操作类型 ('sync', 'test', 'delete')
   * @returns {Promise<Object>} 批量操作结果
   */
  async batchOperation(batchData) {
    try {
      const response = await apiRequest.post('/api/email-accounts/batch-operation/', batchData)
      return response.data
    } catch (error) {
      console.error('批量操作邮件账户失败:', error)
      throw error
    }
  }

  // ========== 自定义字段相关方法 ==========

  /**
   * 获取自定义字段列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} 自定义字段列表
   */
  async getCustomFields(params = {}) {
    try {
      const response = await apiRequest.get('/api/email_accounts/v2/custom-fields/', {
        params: {
          search: params.search || '',
          field_type: params.field_type || '',
          is_active: params.is_active,
          is_list_display: params.is_list_display,
          page: params.page || 1,
          page_size: params.page_size || 100,
          ordering: params.ordering || 'sort_order'
        }
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取自定义字段列表失败:', error)
      throw error
    }
  }

  /**
   * 创建自定义字段
   * @param {Object} fieldData - 字段数据
   * @returns {Promise<Object>} 创建的字段
   */
  async createCustomField(fieldData) {
    try {
      const response = await apiRequest.post('/api/email_accounts/v2/custom-fields/', fieldData)
      return {
        success: true,
        data: response.data,
        message: '自定义字段创建成功'
      }
    } catch (error) {
      console.error('创建自定义字段失败:', error)
      throw error
    }
  }

  /**
   * 更新自定义字段
   * @param {number} fieldId - 字段ID
   * @param {Object} fieldData - 更新的字段数据
   * @returns {Promise<Object>} 更新的字段
   */
  async updateCustomField(fieldId, fieldData) {
    try {
      const response = await apiRequest.put(`/api/email_accounts/v2/custom-fields/${fieldId}/`, fieldData)
      return {
        success: true,
        data: response.data,
        message: '自定义字段更新成功'
      }
    } catch (error) {
      console.error('更新自定义字段失败:', error)
      throw error
    }
  }

  /**
   * 删除自定义字段
   * @param {number} fieldId - 字段ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteCustomField(fieldId) {
    try {
      await apiRequest.delete(`/api/email_accounts/v2/custom-fields/${fieldId}/`)
      return {
        success: true,
        message: '自定义字段删除成功'
      }
    } catch (error) {
      console.error('删除自定义字段失败:', error)
      throw error
    }
  }

  // ========== 列表字段配置相关方法 ==========

  /**
   * 获取可见字段配置
   * @returns {Promise<Object>} 可见字段配置
   */
  async getVisibleFieldConfigs() {
    try {
      const response = await apiRequest.get('/api/email_accounts/v2/field-configs/visible_fields/')
      return {
        success: true,
        data: response.data.data || []
      }
    } catch (error) {
      console.error('获取可见字段配置失败:', error)
      throw error
    }
  }

  /**
   * 获取所有字段配置
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} 字段配置列表
   */
  async getFieldConfigs(params = {}) {
    try {
      const response = await apiRequest.get('/api/email_accounts/v2/field-configs/', {
        params: {
          field_type: params.field_type || '',
          is_visible: params.is_visible,
          page: params.page || 1,
          page_size: params.page_size || 100,
          ordering: params.ordering || 'sort_order'
        }
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取字段配置列表失败:', error)
      throw error
    }
  }

  /**
   * 批量更新字段配置
   * @param {Array} configs - 字段配置数组
   * @returns {Promise<Object>} 更新结果
   */
  async batchUpdateFieldConfigs(configs) {
    try {
      const response = await apiRequest.post('/api/email_accounts/v2/field-configs/batch_update/', {
        configs: configs
      })
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message || '字段配置更新成功'
      }
    } catch (error) {
      console.error('批量更新字段配置失败:', error)
      throw error
    }
  }

  /**
   * 初始化用户字段配置
   * @returns {Promise<Object>} 初始化结果
   */
  async initializeFieldConfigs() {
    try {
      const response = await apiRequest.post('/api/email_accounts/v2/field-configs/initialize/')
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message || '字段配置初始化成功'
      }
    } catch (error) {
      console.error('初始化字段配置失败:', error)
      throw error
    }
  }

  // ===== 邮件账户分组管理 =====

  /**
   * 获取邮件账户分组列表
   * @returns {Promise<Array>} 分组列表
   */
  async getEmailAccountGroups(params = {}) {
    try {
      const response = await apiRequest.get('/api/email_accounts/v2/groups/', { params })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取邮件账户分组失败:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * 创建邮件账户分组
   * @param {Object} groupData 分组数据
   * @returns {Promise<Object>} 创建的分组
   */
  async createEmailAccountGroup(groupData) {
    try {
      const response = await apiRequest.post('/api/email_accounts/v2/groups/', groupData)
      return response.data
    } catch (error) {
      console.error('创建邮件账户分组失败:', error)
      throw error
    }
  }

  /**
   * 更新邮件账户分组
   * @param {number} groupId 分组ID
   * @param {Object} groupData 分组数据
   * @returns {Promise<Object>} 更新的分组
   */
  async updateEmailAccountGroup(groupId, groupData) {
    try {
      const response = await apiRequest.put(`/api/email_accounts/v2/groups/${groupId}/`, groupData)
      return response.data
    } catch (error) {
      console.error('更新邮件账户分组失败:', error)
      throw error
    }
  }

  /**
   * 删除邮件账户分组
   * @param {number} groupId 分组ID
   * @returns {Promise<void>}
   */
  async deleteEmailAccountGroup(groupId) {
    try {
      await apiRequest.delete(`/api/email_accounts/v2/groups/${groupId}/`)
    } catch (error) {
      console.error('删除邮件账户分组失败:', error)
      throw error
    }
  }

  /**
   * 向分组添加邮件账户
   * @param {number} groupId 分组ID
   * @param {Array<number>} accountIds 账户ID列表
   * @returns {Promise<Object>} 操作结果
   */
  async addAccountsToGroup(groupId, accountIds) {
    try {
      const response = await apiRequest.post(`/api/email_accounts/v2/groups/${groupId}/add_accounts/`, {
        account_ids: accountIds
      })
      return response.data
    } catch (error) {
      console.error('向分组添加邮件账户失败:', error)
      throw error
    }
  }

  /**
   * 从分组移除邮件账户
   * @param {number} groupId 分组ID
   * @param {Array<number>} accountIds 账户ID列表
   * @returns {Promise<Object>} 操作结果
   */
  async removeAccountsFromGroup(groupId, accountIds) {
    try {
      const response = await apiRequest.post(`/api/email_accounts/v2/groups/${groupId}/remove_accounts/`, {
        account_ids: accountIds
      })
      return response.data
    } catch (error) {
      console.error('从分组移除邮件账户失败:', error)
      throw error
    }
  }

  // ========== 认证管理相关方法 ==========

  /**
   * 更新邮箱账户密码
   * @param {number} accountId - 账户ID
   * @param {string} password - 新密码
   * @returns {Promise<Object>} 更新结果
   */
  async updatePassword(accountId, password) {
    try {
      const response = await apiRequest.post(`/api/email_accounts/v2/accounts/${accountId}/update-password/`, {
        password
      })
      return response.data
    } catch (error) {
      console.error('更新密码失败:', error)
      throw error
    }
  }

  /**
   * 测试邮箱连接
   * @param {number} accountId - 账户ID
   * @returns {Promise<Object>} 测试结果
   */
  async testConnection(accountId) {
    try {
      const response = await apiRequest.post(`/api/email_accounts/v2/accounts/${accountId}/test_connection/`)
      return response.data
    } catch (error) {
      console.error('测试连接失败:', error)
      throw error
    }
  }

  /**
   * 发起OAuth重新授权
   * @param {number} accountId - 账户ID
   * @returns {Promise<Object>} 授权URL等信息
   */
  async reauthorize(accountId) {
    try {
      const response = await apiRequest.post(`/api/email_accounts/v2/accounts/${accountId}/reauthorize/`)
      return response.data
    } catch (error) {
      console.error('重新授权失败:', error)
      throw error
    }
  }

  /**
   * 获取账户认证状态
   * @param {number} accountId - 账户ID
   * @returns {Promise<Object>} 认证状态信息
   */
  async getAuthStatus(accountId) {
    try {
      const response = await apiRequest.get(`/api/email_accounts/v2/accounts/${accountId}/auth-status/`)
      return response.data
    } catch (error) {
      console.error('获取认证状态失败:', error)
      throw error
    }
  }

  /**
   * 恢复启用被停用/自动禁用的账户（同时清空错误计数）
   * @param {number} accountId - 账户ID
   * @returns {Promise<Object>} 恢复结果
   */
  async restoreAccount(accountId) {
    try {
      const response = await apiRequest.post(`/api/email_accounts/v2/accounts/${accountId}/restore/`)
      return response.data
    } catch (error) {
      console.error('恢复启用账户失败:', error)
      // 透出后端的具体原因（如子账户无权限、账户已启用）
      throw new Error(error.response?.data?.message || '恢复启用失败，请稍后重试')
    }
  }
}

// 导出单例实例
export default new EmailAccountService()
